/**
 * Record search. Sits with the searchable-fields feature because the search
 * behaviour is defined by that feature's config (searchable_fields_config).
 *
 * `searchRecordIds` delegates the JSONB scan to the `search_record_ids` RPC
 * (SECURITY INVOKER, RLS-scoped, metacharacter-safe). Callers feed the result
 * into a records query via .in('id', …) so list filters + pagination still
 * apply on top.
 */
import { supabase } from '@/lib/supabase/client'

export interface SearchableField {
  id: string
  source_form_id: string
  source_field_key: string
  label: string
  is_displayed_in_list: boolean
  display_order: number
}

function parseTitle(v: unknown): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') return (v as Record<string, string>).fa ?? ''
  return ''
}

/** The fields a project has marked searchable, ordered for display. */
export async function fetchSearchableFields(projectId: string): Promise<SearchableField[]> {
  const { data, error } = await supabase
    .from('searchable_fields_config')
    .select('id, source_form_id, source_field_key, display_label, is_displayed_in_list, display_order')
    .eq('project_id', projectId)
    .order('display_order')
  if (error) throw error
  return (data ?? []).map((f) => ({
    id: f.id,
    source_form_id: f.source_form_id,
    source_field_key: f.source_field_key,
    label: parseTitle(f.display_label),
    is_displayed_in_list: f.is_displayed_in_list,
    display_order: f.display_order,
  }))
}

/**
 * Record ids matching `query` by display_id or any configured searchable field
 * value. Returns null when the query is blank (caller should skip id-filtering
 * and list everything).
 *
 * Matching is `field_values->>key ILIKE` — i.e. raw JSONB-text. It works well
 * for text/number fields, but NOT as a user intuits for:
 *   • booleans — stored as `true`/`false`, never the display labels "بله"/"خیر"
 *   • arrays/objects (multi-select, file refs) — `->>` yields NULL, never matches
 * Steer admins toward text/number fields when configuring searchable fields.
 */
export async function searchRecordIds(
  projectId: string,
  query: string,
): Promise<string[] | null> {
  if (!query.trim()) return null
  const { data, error } = await supabase.rpc('search_record_ids', {
    p_project_id: projectId,
    p_query: query,
  })
  if (error) throw error
  return (data ?? []).map((r: { record_id: string }) => r.record_id)
}
